import type { ClickReleaseConfig } from '../config/types';
import type { ReleaseEntry } from '../content/types';
import { el } from '../util/dom';
import { htmlToText } from '../util/html';
import { enter, leave } from '../anim/transition';

const PREVIEW_MAX = 120;

/**
 * A corner notification: tag + title + body preview + a "Show more" CTA that opens the full
 * changelog. Placement and auto-dismiss duration come from config; hovering pauses the timer.
 * A single toast is shown at a time — a new `show()` replaces the current one.
 */
export class Toast {
  #root: ShadowRoot;
  #onShowMore: () => void;
  #node: HTMLElement | null = null;
  #timer: ReturnType<typeof setTimeout> | null = null;

  constructor(root: ShadowRoot, onShowMore: () => void) {
    this.#root = root;
    this.#onShowMore = onShowMore;
  }

  show(entry: ReleaseEntry | null, config: ClickReleaseConfig): void {
    this.#dismissImmediate();

    const toast = el('div', { class: `cr-toast cr-toast--${config.toast.placement}`, role: 'status' });
    const main = el('div', { class: 'cr-toast__main' });

    const tag = entry?.tags?.[0];
    if (tag) main.append(el('span', { class: 'cr-tag' }, [tag]));

    main.append(el('div', { class: 'cr-toast__title' }, [entry?.title ?? config.title]));

    if (entry) {
      const preview = this.#preview(entry.html);
      if (preview) main.append(el('div', { class: 'cr-toast__body' }, [preview]));

      const actions = el('div', { class: 'cr-toast__actions' });
      const cta = el('button', { class: 'cr-toast__cta', type: 'button' }, ['Show more']);
      cta.addEventListener('click', () => {
        this.#onShowMore();
        this.hide();
      });
      actions.append(cta);
      main.append(actions);
    }
    toast.append(main);

    const closeBtn = el('button', { class: 'cr-toast__close', type: 'button', 'aria-label': 'Dismiss' }, ['×']);
    closeBtn.addEventListener('click', () => this.hide());
    toast.append(closeBtn);

    this.#root.append(toast);
    this.#node = toast;
    enter(toast);

    if (config.toast.duration > 0) {
      const start = () => {
        this.#timer = setTimeout(() => this.hide(), config.toast.duration);
      };
      start();
      // Pause auto-dismiss while the pointer is over the toast so it can be read / acted on.
      toast.addEventListener('mouseenter', () => this.#clearTimer());
      toast.addEventListener('mouseleave', () => {
        this.#clearTimer();
        start();
      });
    }
  }

  hide(): void {
    if (!this.#node) return;
    const node = this.#node;
    this.#node = null;
    this.#clearTimer();
    leave(node, 'is-open', () => node.remove());
  }

  destroy(): void {
    this.#dismissImmediate();
  }

  #preview(html: string): string {
    const text = htmlToText(html);
    return text.length > PREVIEW_MAX ? `${text.slice(0, PREVIEW_MAX - 1).trimEnd()}…` : text;
  }

  #dismissImmediate(): void {
    this.#clearTimer();
    this.#node?.remove();
    this.#node = null;
  }

  #clearTimer(): void {
    if (this.#timer) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
  }
}
